import { Divider, FormSection } from '@ksyos/design-system';
import type { TreatmentData } from '@common/validation/customControls/osas/osasAllTreatmentsSchemas/types';
import type { TreatmentOptionKey } from '@common/validation/customControls/osas/osasTreatmentChoice/osasTreatmentChoiceReadSchema';
import type {
    OsasTreatmentData,
    TreatmentFollowUpData,
} from '@common/validation/customControls/osas/osasTreatmentChoice/types';
import { CpapOption } from '@common/enums';
import { treatmentOptionsLabels } from '@common/validation/customControls/osas/osasTreatmentChoice/osasTreatmentChoiceReadSchema';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { getSelectedLanguage } from '@frontend/translate';
import { ReferRead } from './ReferRead';
import { treatmentOptions } from './utils';

type OsasTreatmentReadProps = {
    cpapSetting?: string;
    cpapSettingText?: string;
    refer?: OsasTreatmentData['refer'];
    shouldHaveFollowingExamination?: boolean;
    shouldHaveFollowUpConsult?: boolean;
    taskFollowingExaminationOrConsultWeeks?: number;
    treatment: TreatmentData;
    treatmentFollowUps?: TreatmentFollowUpData;
};

export function OsasTreatmentRead(props: Readonly<OsasTreatmentReadProps>) {
    const { treatment, treatmentFollowUps, cpapSetting, cpapSettingText, refer } = props;

    const keysInOrder = ['SPT', 'MRA', 'CPAP', 'lifestyle', 'refer', 'other'];

    const translate = getSelectedLanguage();

    const treatmentsToRender = keysInOrder
        .filter((key) => !!treatment[key as keyof TreatmentData])
        .map((key) => {
            const value = treatment[key as keyof TreatmentData];
            let followups = null;
            if (treatmentFollowUps && ['CPAP', 'MRA', 'SPT'].includes(key)) {
                const followUpOptions = treatmentOptions[key as TreatmentOptionKey].find(
                    (item) => item.label === value
                )?.followUp;

                followups = (treatmentFollowUps[key as keyof TreatmentFollowUpData] ?? []).map((followup) => {
                    const followUpSettings = followUpOptions?.find((fu) => fu.performer === followup.performer);

                    if (followUpSettings?.hide) {
                        return null;
                    }

                    return (
                        <FormParagraphFormGroup
                            key={`${key}-${followup.performer}`}
                            label={`Vervolgafspraak bij ${followup.performer}`}
                        >
                            {`${followup.weeks} weken`}
                        </FormParagraphFormGroup>
                    );
                });
            }

            if (key === 'refer') {
                return (
                    <ReferRead
                        key={key}
                        refer={refer}
                    />
                );
            }

            return (
                <FormSection
                    isBorderless={true}
                    key={key}
                    title={treatmentOptionsLabels[key as TreatmentOptionKey]}
                >
                    <FormParagraphFormGroup label="Type behandeling">{value}</FormParagraphFormGroup>
                    {followups}

                    {key === 'CPAP' ? renderCpapSetting() : null}

                    {key === 'lifestyle' ? renderLifestyleSetting() : null}
                </FormSection>
            );
        });

    if (treatmentsToRender.length === 0) {
        return null;
    }

    return (
        <>
            {treatmentsToRender}
            <Divider spacing="medium" />
        </>
    );

    function renderCpapSetting() {
        if (cpapSetting === CpapOption.custom && cpapSettingText) {
            return <FormParagraphFormGroup label="CPAP instellingen">{cpapSettingText}</FormParagraphFormGroup>;
        }

        return <FormParagraphFormGroup label="CPAP instellingen">{cpapSetting}</FormParagraphFormGroup>;
    }

    function renderLifestyleSetting() {
        if (!props.taskFollowingExaminationOrConsultWeeks) {
            return null;
        }

        return (
            <>
                <FormParagraphFormGroup label="Vervolg slaaponderzoek">
                    {props.shouldHaveFollowingExamination ? translate.common.yes : translate.common.no}
                </FormParagraphFormGroup>
                <FormParagraphFormGroup label="Vervolg consult">
                    {props.shouldHaveFollowUpConsult ? translate.common.yes : translate.common.no}
                </FormParagraphFormGroup>
                <FormParagraphFormGroup
                    label={props.shouldHaveFollowingExamination ? 'Vervolgonderzoek na' : 'Vervolgconsult na'}
                >
                    {`${props.taskFollowingExaminationOrConsultWeeks}`}
                </FormParagraphFormGroup>
            </>
        );
    }
}
